<template>
    <div>
        <table class="tracking-listing">
            <thead>
                <tr>
                    <th>{{ physicianHeading }}</th>
                    <th v-if="!isTablet">{{ $t('pages.referrals.index.headings.patient') }}</th>
                    <th v-if="!isTablet">{{ $t('pages.referrals.index.headings.scheduled_for') }}</th>
                    <th v-if="!isTablet">{{ $t('pages.referrals.index.headings.status') }}</th>
                    <th>
                        <div class="tracking-listing__filter">
                            <common-filter
                                :title="$t('pages.referrals.index.filters.title')"
                                :options="filterOptions"
                                @filter-changed="filterChange"
                            ></common-filter>

                            <common-icon
                                class="tracking-listing__print"
                                name="print"
                                size="large"
                                @click="printPage"
                            ></common-icon>
                        </div>
                    </th>
                </tr>
            </thead>

            <tbody>
                <tr v-for="(referral, index) in referrals" :key="index">
                    <td>
                        <practice-details
                            v-if="showPracticeDetails(referral)"
                            :practice="getPracticeDetails(referral)"
                        ></practice-details>
                        <user-details
                            v-else-if="!isFromPatient"
                            :user="physicianData(referral)"
                            :bottomText="physicianSpeciality(referral)"
                            :bottomText2="getCreatedDate(referral)"
                        ></user-details>
                        <patient-details
                            v-else
                            :user="patientData(referral)"
                            :bottomText2="getCreatedDate(referral)"
                        ></patient-details>
                    </td>

                    <td v-if="!isTablet">
                        <div class="tracking-listing__patient">
                            <div :class="patientDataClass(referral)">
                                <p class="user-details-card__highlited" @click="openReferralInfoModal(referral)">
                                    {{ getUserFullName(referral.patient) }}
                                </p>
                                <p>{{ $t('pages.referrals.index.patient.dob') }}</p>
                                <p>{{ getPatientDateOfBirth(referral.patient) }}</p>
                            </div>
                            <div class="tracking-listing__patient--tooltip">
                                <common-info-tip
                                    v-if="!referral.shouldShowPatientInfo"
                                    class="tracking-listing__patient--tooltip-text"
                                >
                                    {{ $t('pages.referrals.index.patient.tooltip') }}
                                </common-info-tip>
                            </div>
                        </div>
                    </td>

                    <td v-if="!isTablet">
                        <listing-datetime
                            :datetime="referral.appointmentDateTime"
                            :icon="getAppointmentDateIcon(referral)"
                            :bottomText="getAppointmentLocation(referral)"
                        ></listing-datetime>
                    </td>

                    <td v-if="!isTablet" class="tracking-listing__status">
                        <referrals-status
                            :referral="referral"
                            :isStatusChangeable="true"
                            @status-updated="updateReferralStatus"
                        ></referrals-status>
                    </td>

                    <td class="listing-listing__action">
                        <referrals-status
                            v-if="isTablet"
                            :showDescriptionText="false"
                            :referral="referral"
                            :isStatusChangeable="true"
                            @status-updated="updateReferralStatus"
                        ></referrals-status>
                        <listing-action :type="$route.params.type" :referral="referral"></listing-action>
                    </td>
                </tr>
            </tbody>
        </table>
        <referrals-patient-details-modal
            v-if="showPatientDetailsModal"
            :referral="activeReferral"
            @close="showPatientDetailsModal = false"
        ></referrals-patient-details-modal>
    </div>
</template>

<script>
    import dayjs from 'dayjs'
    import ReferralsStatus from 'components/page-elements/ReferralsStatus'
    import UserDetails from 'components/page-elements/referrals/details/UserDetailsCard'
    import PracticeDetails from 'components/page-elements/referrals/details/PracticeDetailsCard'
    import ListingDatetime from 'components/page-elements/referrals-index-listing/ListingDatetime'
    import ListingAction from 'components/page-elements/referrals-index-listing/ListingAction'
    import DateTimeMixin from 'mixins/DateTimeMixin'
    import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralsPatientDetailsModal from 'components/page-elements/referrals/ReferralsPatientDetailsModal'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ReferralPathTypeEnum from '@/enums/ReferralPathTypeEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import PatientDetails from 'components/page-elements/referrals/details/PatientDetailsCard'

    export default {
        mixins: [DateTimeMixin, ScreenSizeDetectionMixin],

        components: {
            ReferralsStatus,
            UserDetails,
            ListingDatetime,
            ListingAction,
            ReferralsPatientDetailsModal,
            PracticeDetails,
            PatientDetails,
        },

        props: {
            type: { type: String, required: true },
            referrals: { type: Array, required: true },
            filterOptions: { type: Array, required: true },
            isFromPatient: { type: Boolean, required: false, default: false },
        },

        data() {
            return {
                showPatientDetailsModal: false,
                activeReferral: {},
                PracticeTypeEnum: PracticeTypeEnum,
                practiceData: {},
            }
        },

        computed: {
            physicianHeading() {
                return this.$t(
                    this.type === ReferralPathTypeEnum.RECEIVED
                        ? 'pages.referrals.index.headings.from'
                        : 'pages.referrals.index.headings.to',
                )
            },
        },

        methods: {
            updateReferralStatus(updatedReferral) {
                const index = this.referrals.findIndex((r) => r.id === updatedReferral.id)

                if (index !== -1) {
                    this.$set(this.referrals, index, {
                        ...this.referrals[index],
                        status: updatedReferral.status,
                    })
                }
            },
            showPracticeDetails(referral) {
                if (this.type === ReferralPathTypeEnum.RECEIVED) {
                    return referral.fromPractice?.practiceType === PracticeTypeEnum.ANCILLARY
                } else {
                    return referral.practice.practiceType === PracticeTypeEnum.ANCILLARY
                }
            },
            getPracticeDetails(referral) {
                if (this.type === ReferralPathTypeEnum.RECEIVED) {
                    return referral.fromPractice
                } else {
                    return referral.practice
                }
            },
            physicianData(referral) {
                return this.type === ReferralPathTypeEnum.RECEIVED ? referral.fromUser : referral.toUser
            },
            patientData(referral) {
                return referral.patient || {}
            },
            physicianSpeciality(referral) {
                return this.$auth.getUser().id === referral.fromUser?.id
                    ? referral.toUser?.specialities[0]?.speciality?.name
                    : referral.fromUser?.specialities[0]?.speciality?.name
            },
            getAppointmentDateIcon(referral) {
                const icon = {
                    name: 'white-warning',
                    size: 'medium',
                    hintHeader: this.$t('pages.referrals.index.warnings.reschedule.hintTitle'),
                    hintText: this.$t('pages.referrals.index.warnings.reschedule.text'),
                }
                const isReschedule = referral.status === 'RESCHEDULE_AVAILABILITY_CHANGE'
                const appointmentDateTime = dayjs(referral.appointmentDateTime)
                const isInNext24h = dayjs().isBefore(appointmentDateTime) && dayjs().add(24, 'h').isAfter(appointmentDateTime)
                return isReschedule && isInNext24h ? icon : null
            },

            getCreatedDate(referral) {
                return dayjs(referral.createdDateTime).format('MMMM D, YYYY')
            },

            openReferralInfoModal(referral) {
                ReferralsAPI.get(referral.id, this.$auth.getActiveMember().id).then((response) => {
                    this.activeReferral = response
                    this.showPatientDetailsModal = true
                })
            },

            getAppointmentLocation(referral) {
                if (referral.practiceLocation && referral.practiceLocation.name) {
                    return referral.practiceLocation.name
                }
                return this.$t('pages.referrals.index.locations.main')
            },

            getUserFullName(patient) {
                return `${patient.firstName} ${patient.lastName}`
            },

            getPatientDateOfBirth(patient) {
                return dayjs(patient.dateOfBirth).format('MM/DD/YYYY')
            },

            patientDataClass(referral) {
                return {
                    'text-blured': !referral.shouldShowPatientInfo,
                }
            },

            filterChange(filterData) {
                const status = filterData
                    .filter((option) => option.value)
                    .map((option) => option.status)
                    .toString()
                this.$router.push({
                    ...this.$route,
                    query: { ...this.$route.query, status },
                })
            },

            printPage() {
                window.print()
            },
        },
    }
</script>
